import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'
import Const from 'constants/constant'
import EventEmitter from 'events'
import { LocalAuthStorageKey, TokenBundle } from 'types'
import Utils from 'utils'

interface ApiErrorResponse {
  code: number
  error: {
    key: string
    type: string
    message: string
  }
}

export const emit = new EventEmitter()

export const apiDomain = process.env.REACT_APP_API_URL
export const iijApiDomain = process.env.REACT_APP_IIJ_API_URL

export const storeIdTokens = (tokenBundle: TokenBundle) => {
  localStorage.setItem(LocalAuthStorageKey.ID_TOKEN, tokenBundle.idToken)
  if (tokenBundle.refreshToken) {
    localStorage.setItem(
      LocalAuthStorageKey.REFRESH_TOKEN,
      tokenBundle.refreshToken
    )
  }
}

export const clearIdTokens = () => {
  localStorage.removeItem(LocalAuthStorageKey.REFRESH_TOKEN)
  localStorage.removeItem(LocalAuthStorageKey.ID_TOKEN)
  Utils.deleteCookie(LocalAuthStorageKey.REFRESH_TOKEN)
  Utils.deleteCookie(LocalAuthStorageKey.ID_TOKEN)
}

let refreshTokenRequest: null | Promise<any> = null

export const getToken = () => {
  const refreshToken =
    localStorage.getItem(LocalAuthStorageKey.REFRESH_TOKEN) ||
    Utils.getCookie(LocalAuthStorageKey.REFRESH_TOKEN)
  const idToken =
    localStorage.getItem(LocalAuthStorageKey.ID_TOKEN) ||
    Utils.getCookie(LocalAuthStorageKey.ID_TOKEN)
  return { refreshToken, idToken }
}

const instance = axios.create({
  baseURL: apiDomain,
  headers: {
    'Content-Type': 'application/json'
  }
})

export const refreshToken = async () => {
  try {
    const { refreshToken } = getToken()

    const response = await axios
      .create({
        baseURL: apiDomain
      })
      .post(`/v1/auth/token/refresh`, {
        refreshToken
      })

    storeIdTokens(response.data.data)
    refreshTokenRequest = null

    // Update axios instance with the new token
    instance.defaults.headers.common.Authorization = `Bearer ${response.data.data.idToken}`
  } catch (error: any) {
    refreshTokenRequest = null
    clearIdTokens()

    // TODO: BE need to change to 401
    if (error?.response?.status === 400) {
      emit.emit(Const.UNAUTHORIZED_ERROR)
    }

    return Promise.reject(error)
  }
}

const refresh = async () => {
  try {
    const { refreshToken } = getToken()

    const response = await axios
      .create({
        baseURL: apiDomain
      })
      .post(`/v1/auth/token/refresh`, {
        refreshToken
      })
    storeIdTokens(response.data.data)
    refreshTokenRequest = null

    return response.data.idToken
  } catch (error: any) {
    refreshTokenRequest = null
    clearIdTokens()

    // TODO: BE need to change to 401
    if (error?.response?.status === 400) {
      emit.emit(Const.UNAUTHORIZED_ERROR)
    }

    return Promise.reject(error)
  }
}

const onRequest: any = (config: AxiosRequestConfig): AxiosRequestConfig => {
  const { idToken } = getToken()
  if (config.headers) config.headers.Authorization = `Bearer ${idToken}`
  return config
}

const onRequestError = (error: AxiosError): Promise<AxiosError> => {
  return Promise.reject(error)
}

const onResponse = (response: AxiosResponse): AxiosResponse => {
  return response
}

const onResponseError = async (error: AxiosError): Promise<any> => {
  const apiError = (error as AxiosError<ApiErrorResponse>).response?.data?.error
  if (
    error.response &&
    error.response.status === 401 &&
    error.config &&
    !error.config.url?.includes('login') &&
    apiError?.key === 'error_invalid_token'
  ) {
    refreshTokenRequest = refreshTokenRequest || refresh()
    const newIdToken = await refreshTokenRequest
    error.config.headers = {
      ...(error.config.headers as any),
      authorization: `Bearer ${newIdToken}`
    }
    refreshTokenRequest = null
    return instance(error.config)
  }
  return Promise.reject(error)
}

instance.interceptors.request.use(onRequest, onRequestError)
instance.interceptors.response.use(onResponse, onResponseError)

export default instance
